import { Modal } from "react-bootstrap";
import { useGetParentPaymentSummaryDetailQuery } from "../../../store/api/school/PaymentHistorySlice";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";
import { useTranslation } from "react-i18next";

const statusClass = (status) => {
  if (!status) return "status unavailable";
  const s = status.toLowerCase();
  if (s === "completed" || s === "paid") return "status bg-success text-white";
  if (s === "cancelled" || s === "rejected")
    return "status bg-danger text-white";
  if (s === "partial") return "status bg-warning text-dark";
  if (s === "created" || s === "pending") return "status unavailable";
  return "status unavailable";
};

const paymentTypeLabel = (type) => {
  if (type === "full_payment") return "Full Payment";
  if (type === "partial_payment") return "Partial Payment";
  if (type === "student_fee") return "Student Fee";
  return type || "—";
};

const EffectiveBankCell = ({ effectiveBank }) => {
  if (!effectiveBank) return <span className="text-muted">—</span>;
  return (
    <span className="d-flex align-items-center gap-1 flex-wrap">
      {effectiveBank.bank_name || "—"}
      {effectiveBank.routed_through_admin && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 10,
            background: "#fde8c8",
            color: "#c8900a",
            border: "1px solid #f0b342",
            whiteSpace: "nowrap",
          }}
        >
          via Admin
        </span>
      )}
    </span>
  );
};

const AmountCard = ({ label, value, color }) => (
  <div
    style={{
      background: "#fff",
      border: `1.5px solid ${color}22`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 8,
      padding: "10px 14px",
      flex: 1,
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "#94a3b8",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div
    className="d-flex align-items-start py-2"
    style={{ borderBottom: "1px solid #f1f5f9" }}
  >
    <span
      style={{
        minWidth: 130,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span style={{ fontSize: 13, color: "#1e293b", wordBreak: "break-word" }}>
      {value ?? "—"}
    </span>
  </div>
);

const ParentPaymentDetailModal = ({ summaryId, onClose }) => {
  const { data, isLoading, isError } = useGetParentPaymentSummaryDetailQuery(
    summaryId,
    { skip: !summaryId },
  );
  const { t } = useTranslation();
  const detail = data?.data;

  return (
    <Modal show={!!summaryId} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header
        closeButton
        style={{
          background: "#1e293b",
          color: "#fff",
          borderBottom: "none",
          padding: "16px 24px",
        }}
      >
        <Modal.Title
          style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}
        >
          {t("parent")} {t("payment")} {t("detail")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px 24px", background: "#f8fafc" }}>
        {isLoading && (
          <div className="text-center py-5">
            <span className="spinner-border spinner-border-sm me-2" />
            <span style={{ color: "#64748b" }}>{t("loading")}...</span>
          </div>
        )}
        {isError && (
          <div className="alert alert-danger">
            <p>{`${t("failed")} ${t("to")} ${t("load")} ${t("details")}`}</p>
          </div>
        )}
        {detail &&
          (() => {
            const currSym = (c) => (c === "CAD" ? "C$" : "$");
            const detailCurrency =
              detail.payments?.[0]?.currency ||
              detail.transactions?.[0]?.currency ||
              "USD";
            const sym = currSym(detailCurrency);

            return (
              <>
                {/* Amount Summary Cards */}
                <div className="d-flex gap-2 flex-wrap mb-3">
                  <AmountCard
                    label={`${t("total")} ${t("amount")}`}
                    value={`${sym}${parseFloat(detail.summary.total_amount || 0).toFixed(2)}`}
                    color="#3b82f6"
                  />
                  <AmountCard
                    label={`${t("paid")} ${t("amount")}`}
                    value={`${sym}${parseFloat(detail.summary.paid_amount || 0).toFixed(2)}`}
                    color="#22c55e"
                  />

                  <AmountCard
                    label={`${t("due")} ${t("amount")}`}
                    value={`${sym}${parseFloat(detail.summary.due_amount || 0).toFixed(2)}`}
                    color="#f59e0b"
                  />
                </div>

                {/* Info Cards Row */}
                <div className="row g-3 mb-3">
                  {/* Parent Info */}
                  <div className="col-12 col-md-6">
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: "14px 16px",
                        border: "1px solid #e2e8f0",
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 8,
                        }}
                      >
                        {t("parent")} {t("info")}
                      </div>
                      <InfoRow
                        label={`${t("parent")} ${t("name")}`}
                        value={detail.summary.parent_name}
                      />
                      <InfoRow
                        label={t("email")}
                        value={detail.summary.parent_email}
                      />
                      <InfoRow
                        label={`${t("created")} ${t("by")}`}
                        value={`${detail.summary.creator.name} (${detail.summary.creator.role})`}
                      />
                    </div>
                  </div>

                  {/* Payment Status Info */}
                  <div className="col-12 col-md-6">
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: "14px 16px",
                        border: "1px solid #e2e8f0",
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 8,
                        }}
                      >
                        {t("payment")} {t("summary")}
                      </div>
                      <InfoRow
                        label={t("status")}
                        value={
                          <span
                            className={statusClass(
                              detail.summary.payment_status,
                            )}
                          >
                            {capitalizeFirstLetter(
                              detail.summary.payment_status,
                            )}
                          </span>
                        }
                      />
                      <InfoRow
                        label={`${t("remaining")} ${t("inst")}`}
                        value={detail.summary.remaining_installments}
                      />
                      <InfoRow
                        label={`${t("last")} ${t("payment")}`}
                        value={formatDateToLocal(
                          detail.summary.last_payment_date,
                        )}
                      />
                      <InfoRow
                        label={`${t("next")} ${t("due")}`}
                        value={formatDateToLocal(
                          detail.summary.next_payment_due,
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Transactions */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      {t("transactions")}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: "#e2e8f0",
                        color: "#475569",
                        borderRadius: 20,
                        padding: "1px 8px",
                      }}
                    >
                      {detail.transactions.length}
                    </span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <div className="table m-0">
                      <table style={{ minWidth: 640 }}>
                        <thead>
                          <tr>
                            <th>{t("sr")}</th>
                            <th className="text-nowrap">{t("orderNo")}</th>
                            <th className="text-nowrap">{t("type")}</th>
                            <th className="text-nowrap">{t("amount")}</th>
                            <th className="text-nowrap">{t("bankUsed")}</th>
                            <th className="text-nowrap">
                              {t("transactionId")}
                            </th>
                            <th className="text-nowrap">{t("status")}</th>
                            <th className="text-nowrap">{t("date")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.transactions.length > 0 ? (
                            detail.transactions.map((txn, i) => (
                              <tr key={txn.id}>
                                <td>{i + 1}</td>
                                <td className="text-nowrap">
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: 11,
                                      background: "#f0f3f6",
                                      padding: "2px 5px",
                                      borderRadius: 3,
                                    }}
                                  >
                                    {txn.order_number}
                                  </span>
                                </td>
                                <td className="text-nowrap">
                                  {paymentTypeLabel(txn.payment_type)}
                                </td>
                                <td className="text-nowrap">
                                  <strong>
                                    {currSym(txn.currency)}
                                    {txn.amount}
                                  </strong>
                                  <small className="ms-1 text-muted">
                                    {txn.currency}
                                  </small>
                                </td>
                                <td className="text-nowrap">
                                  <EffectiveBankCell
                                    effectiveBank={txn.effective_bank}
                                  />
                                </td>
                                <td
                                  className="text-nowrap"
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "monospace",
                                    maxWidth: 160,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {txn.transaction_id || "—"}
                                </td>
                                <td className="text-nowrap">
                                  <span
                                    className={statusClass(txn.payment_status)}
                                  >
                                    {capitalizeFirstLetter(txn.payment_status)}
                                  </span>
                                </td>
                                <td className="text-nowrap">
                                  {formatDateToLocal(txn.created_at)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className="text-center">
                              <td
                                colSpan="8"
                                style={{ padding: "20px 0", color: "#94a3b8" }}
                              >
                                {`${t("no")} ${t("transactions")} ${t("found")}`}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
      </Modal.Body>
    </Modal>
  );
};

export default ParentPaymentDetailModal;
